import React, {useEffect, useMemo, useState} from "react";
import {AutoSizer, Column, Table} from "react-virtualized";
import debounce from 'lodash.debounce';
import { IoMdArrowDroprightCircle, IoMdArrowDropdownCircle } from "react-icons/io";

const PivotTableByPeriod = ({summaryData}) => {
    const [searchTerm, setSearchTerm] = useState('');

    function getWidthOfClass(className) {
        const elements = document.getElementsByClassName(className);
        if (elements.length === 0) return 0;
        const width = elements[0].getBoundingClientRect().width;
        return width > 1920 ? width : 1520;
    }

    const widthOfDiv = getWidthOfClass('summary-report-container');

    const debouncedSearch = useMemo(
        () =>
            debounce((value) => {
                setSearchTerm(value);
            }, 300),
        []
    );

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    const filteredData = useMemo(() => {
        if (!searchTerm) return summaryData;

        const lowercasedTerm = searchTerm.toLowerCase();

        return summaryData.filter((item) => {
            return (
                (item.site && item.site.toLowerCase().includes(lowercasedTerm)) ||
                (item.provider && item.provider.toLowerCase().includes(lowercasedTerm)) ||
                (item.role && item.role.toLowerCase().includes(lowercasedTerm)) ||
                (item.notes && item.notes.toLowerCase().includes(lowercasedTerm))
            );
        });
    }, [summaryData, searchTerm]);

    const rowHighlightStyle = (index) => {
        if (filteredData.length > 0 && index >= 0) {
            const rowData = filteredData[index];
            if (rowData.rowHighlightColour) {
                return { backgroundColor: rowData.rowHighlightColour };
            } else if (rowData.resolutionContent?.includes("Closed")) {
                return { backgroundColor: '#ddd', textDecoration: 'line-through' };
            } else {
                return { backgroundColor: 'white' };
            }
        }
    }

    function transformDataForPivot(resultList) {
            const byPeriod = {};
        for (const row of resultList) {
            if (!byPeriod[row.period]) {
                byPeriod[row.period] = [];
            }
            byPeriod[row.period].push(row);
        }
        
        const flattenedRows = [];

        for (const [period, periodRows] of Object.entries(byPeriod)) {
            flattenedRows.push({
                type: 'period',
                period,
                security: getSumByDept(periodRows, 'Security'),
                entertainment: getSumByDept(periodRows, 'Entertainment'),
                cleaning: getSumByDept(periodRows, 'Cleaning'),
                fm: getSumByDept(periodRows, 'Facilities Management'),
                expanded: false, 
            });

        const byWeek = {};
        for (const row of periodRows) {
            const week = row.weekending.split('T')[0] || 'No-Week';
            if (!byWeek[week]) {
                byWeek[week] = [];
            }
            byWeek[week].push(row);
        }

        for (const [week, weekRows] of Object.entries(byWeek)) {
            flattenedRows.push({
                type: 'week',
                period,
                week,
                security: getSumByDept(weekRows, 'Security'),
                entertainment: getSumByDept(weekRows, 'Entertainment'),
                cleaning: getSumByDept(weekRows, 'Cleaning'),
                fm: getSumByDept(weekRows, 'Facilities Management'),
                expanded: false,
            });

            const bySite = {};

            for (const row of weekRows) {
                if (!bySite[row.site]) {
                    bySite[row.site] = {
                        type: 'site',
                        period,
                        week,
                        site: row.site,
                        security: 0,
                        entertainment: 0,
                        cleaning: 0,
                        fm: 0,
                    };
                }

                switch (row.department) {
                    case 'Security':
                        bySite[row.site].security += (row.nettCost || 0);
                        break;
                    case 'Entertainment':
                        bySite[row.site].entertainment += (row.nettCost || 0);
                        break;
                    case 'Cleaning':
                        bySite[row.site].cleaning += (row.nettCost || 0);
                        break;
                    case 'Facilities Management':
                        bySite[row.site].fm += (row.nettCost || 0);
                        break;
                    default:
                        break;
                }
            }
            
            for (const siteObj of Object.values(bySite)) {
                flattenedRows.push(siteObj);
            }
           }
        }
        return flattenedRows;
    }

    function getSumByDept(rows, departmentName) {
        return rows
            .filter((r) => r.department === departmentName)
            .reduce((sum, r) => sum + (r.nettCost || 0), 0);
    }

    const transformedRows = useMemo(() => {
        return transformDataForPivot(filteredData);
    }, [filteredData]);

    const [expandedMap, setExpandedMap] = useState({});

    const toggleRow = (row) => {
        
        let key = row.type + ':' + row.period;
        if (row.type === 'week') {
            key += ':' + row.week;
        }

        setExpandedMap((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const finalRows = [];
    for (let i = 0; i < transformedRows.length; i++) {
        const row = transformedRows[i];

        if (row.type === 'period') {
            finalRows.push(row);
        }
        if (row.type === 'week') {
            const parentKey = 'period:' + row.period;
            if (expandedMap[parentKey]) {
                finalRows.push(row);
                // for the week’s children (site rows), only push if week is expanded
                const key = 'week:' + row.period + ':' + row.week;
                if (expandedMap[key]) {
                    finalRows.push(...collectWeekSites(transformedRows, row.period, row.week));
                }
            }
        }
    }

    function collectWeekSites(allRows, period, week) {
        return allRows.filter((r) => r.type === 'site' && r.period === period && r.week === week);
    }

    const rowGetter = ({ index }) => finalRows[index];
    const rowCount = finalRows.length;

    const cellRenderer = ({ cellData, rowData }) => {
        if (rowData.type === 'period') {
         
            const key = 'period:' + rowData.period;
            return (
                <div style={{ cursor: 'pointer' }} onClick={() => toggleRow(rowData)}>
                    {expandedMap[key] ? <IoMdArrowDropdownCircle/> : <IoMdArrowDroprightCircle/>} {rowData.period}
                </div>
            );
            
        } else if (rowData.type === 'week') {
            
            const key = 'week:' + rowData.period + ':' + rowData.week;
            return (
                <div style={{ marginLeft: 20, cursor: 'pointer' }} onClick={() => toggleRow(rowData)}>
                    {expandedMap[key] ? <IoMdArrowDropdownCircle/> : <IoMdArrowDroprightCircle/>} {rowData.week}
                </div>
            );
            
        } else if (rowData.type === 'site') {
            
            return (
                <div style={{ marginLeft: 40 }}>
                    {rowData.site}
                </div>
            );
            
        }
        return cellData;
    };

    const deptCellRenderer = ({ rowData, dataKey }) => {
        const value = rowData[dataKey] || 0;
        return (
            <div style={{ textAlign: 'right', paddingRight: '8px' }}>
                £{value.toFixed(2)}
            </div>
        );
    };

    return (
        <AutoSizer>
                {({height}) => (
                    <Table
                        width={widthOfDiv}
                        horizontal={true}
                        scrollingResetTimeInterval={0}
                        height={height}
                        rowHeight={60}
                        rowRender={50}
                        rowCount={rowCount}
                        rowGetter={rowGetter}
                        rowStyle={({index}) => rowHighlightStyle(index)}
                        rowClassName={"mangerTableRow"}
                        layout="horizontal"
                    >

                        <Column
                            width={120}
                            flexGrow={1}
                            label="Period / Week / Site"
                            dataKey="period" 
                            style={{whiteSpace: 'nowrap', textWrap: 'wrap'}}
                            cellRenderer={cellRenderer}
                        />

                                <Column
                            width={120}
                            flexGrow={1}
                            headerStyle={{
                                textAlign: 'center'
                            }}
                            className="d-flex justify-content-center"
                            label="Security"
                            dataKey="security"
                            cellRenderer={deptCellRenderer}
                        />
                        <Column
                            width={140}
                            flexGrow={1}
                            headerStyle={{
                                textAlign: 'center'
                            }}
                            className="d-flex justify-content-center"
                            label="Entertainment"
                            dataKey="entertainment"
                            cellRenderer={deptCellRenderer}
                        />
                        <Column
                            width={120}
                            flexGrow={1}
                            headerStyle={{
                                textAlign: 'center'
                            }}
                            className="d-flex justify-content-center"
                            label="Cleaning"
                            dataKey="cleaning"
                            cellRenderer={deptCellRenderer}
                        />
                        <Column
                            width={120}
                            flexGrow={1}
                            headerStyle={{
                                textAlign: 'center'
                            }}
                            className="d-flex justify-content-center"
                            label="FM"
                            dataKey="fm"
                            cellRenderer={deptCellRenderer}
                        />

                    </Table>
                )}
            </AutoSizer>
    );
}

export default PivotTableByPeriod;